<script setup>
import { ref, computed, onMounted } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import { useOrderStore } from '@/stores/OrderStore';
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { useErrorLogger } from '@/composables/useErrorLogger';

const isSubmitting = ref(false);

const authStore = useAuthStore();
const orderStore = useOrderStore();
const toast = useToast();
const { logError } = useErrorLogger();

const storeId = localStorage.getItem('branchId');

const billBlueprint = [
  { id: 'bill-100', label: '$100', value: 100 },
  { id: 'bill-50', label: '$50', value: 50 },
  { id: 'bill-20', label: '$20', value: 20 },
  { id: 'bill-10', label: '$10', value: 10 },
  { id: 'bill-5', label: '$5', value: 5 }
];

const coinBlueprint = [
  { id: 'coin-2', label: '$2', value: 2 },
  { id: 'coin-1', label: '$1', value: 1 },
  { id: 'coin-25', label: '$0.25', value: 0.25 },
  { id: 'coin-10', label: '$0.10', value: 0.1 },
  { id: 'coin-5', label: '$0.05', value: 0.05 },
  { id: 'coin-1c', label: '$0.01', value: 0.01 },
];

const bills = ref(billBlueprint.map((item) => ({ ...item, quantity: 0 })));
const coins = ref(coinBlueprint.map((item) => ({ ...item, quantity: 0 })));

const currencyFormatter = new Intl.NumberFormat('en-CA', {
  style: 'currency',
  currency: 'CAD'
});

const billTotal = computed(() =>
  bills.value.reduce((sum, item) => sum + item.value * (item.quantity || 0), 0)
);

const coinTotal = computed(() =>
  coins.value.reduce((sum, item) => sum + item.value * (item.quantity || 0), 0)
);

const cashTotal = computed(() => billTotal.value + coinTotal.value);

function formatCurrency(amount) {
  return currencyFormatter.format(amount || 0);
}

function handleQuantityInput(item) {
  if (item.quantity === '' || item.quantity === null || Number.isNaN(item.quantity)) {
    item.quantity = 0;
    return;
  }

  if (item.quantity < 0) {
    item.quantity = 0;
  }
}

function selectQty(event) {
  event.target.select();
}

function lineTotal(item) {
  return item.value * (item.quantity || 0);
}

function clearLine(item) {
  item.quantity = 0;
}

function resetFloat() {
  bills.value.forEach((bill) => {
    bill.quantity = 0;
  });
  coins.value.forEach((coin) => {
    coin.quantity = 0;
  });
}

function toNumber(value) {
  const num = parseFloat(value);
  return Number.isFinite(num) ? num : 0;
}

// Handle submit opeing balance

async function handleSubmit() {
  if (cashTotal.value <= 0) {
    toast.error('Please enter a valid float amount before submitting.');
    return;
  }
  isSubmitting.value = true;
  const userEmail = window.users.find(
    (user) => user.FullName === authStore.getCasherName
  )?.Email || "";

  const payload = {
    user: userEmail,
    date: authStore.today,
    cashFloat: toNumber(cashTotal.value), 
    cashCoins: [
    ...bills.value.map((b) => ({
        cashAndCoin: b.value,
        qty: b.quantity || 0,
        amount: b.value * (b.quantity || 0)
      })),
      ...coins.value.map((c) => ({
        cashAndCoin: c.value,
        qty: c.quantity || 0,
        amount: c.value * (c.quantity || 0)
      }))
    ],
  };

  try {
    const token = localStorage.getItem('token') || localStorage.getItem('managerToken');
    if (!token) {
      console.log('Authentication token is missing. Please log in again.');
      return;
    }

    if (cashTotal.value <= 0) {
      console.log('Please enter a valid float amount before submitting.');
      return;
    }

    const responseError = ref('');

    const response = await axios.post('/api/Opening/NewOpening', payload, {
      headers: {
        'store-id': storeId,
        'Authorization': `Bearer ${token}`
      }
    });

    if (response.status === 200) {
      // Check if the response contains an error status
      if (response.data.status === 'error') {
        const errorMessage = response.data.message || 'Failed to submit opening balance. Please try again.';
        console.error('Opening balance submission error:', errorMessage);
        toast.error(errorMessage);
      } else {
        console.log('Opening balance submitted successfully.');
        toast.success('Opening balance submitted successfully.');
        authStore.openingStatus = 'open';
        handleClose();
      }
    } else {
      console.log('Failed to submit opening balance. Please try again.');
      toast.error('Failed to submit opening balance. Please try again.');
    }

  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to submit opening balance. Please try again.';
    console.error('Error submitting opening balance:', errorMessage);
    toast.error(errorMessage);
    logError({ callStack: error?.stack || String(error), errorDetails: { message: errorMessage, httpStatus: error?.response?.status, component: 'OpeningBalancePopup', action: 'submitOpeningBalance', branchId: authStore.branchId, user: authStore.currentUser } });
  } finally {
    isSubmitting.value = false;
  }
}

function handleClose() {
  authStore.isOpeningBalacePopupVisible = false;
  resetFloat();
}
</script>

<template>
  <Teleport to="body">
    <Transition name="modal-outer">
      <div
        v-if="authStore.isOpeningBalacePopupVisible"
        class="absolute z-10 w-full bg-black bg-opacity-30 h-screen top-0 left-0 flex justify-center items-start md:items-center px-4 md:px-8 py-6 md:py-0"
      >
        <Transition name="modal-inner" class="rounded-2xl">
          <div
            class="fixed top-10 z-50 rounded-2xl border border-gray-100 bg-gradient-to-br from-white via-gray-50 to-gray-100 shadow-xl p-6 md:p-8 w-10/12 max-h-[90vh] overflow-y-auto"
          >
            <div class="flex items-center justify-between mb-8">
              <button
                type="button"
                class="absolute right-0 top-4 pi pi-times px-6 py-2 text-gray-600 font-medium"
                @click.stop="!isSubmitting && handleClose()"
                :disabled="isSubmitting"
                :class="{ 'opacity-40 cursor-not-allowed': isSubmitting }"
              ></button>
              <div>
                <h1 class="text-2xl font-semibold text-gray-900">Opening Balance - Cash Float</h1>
                <p class="text-sm text-gray-500 mt-1">
                  Capture the starting float for this POS profile. Enter bill and coin quantities below.
                </p>
              </div>
            </div>

            <section class="bg-[#f4f5f7] border-gray-100 rounded-3xl px-6 py-6 shadow-sm">
             
              <!-- <div class="flex flex-col gap-2">
                <label class="text-sm font-medium text-gray-600">Auto-Round to $5</label>
                <label class="flex items-center gap-3">
                  <input v-model="autoRoundToFive" type="checkbox" class="toggle" />
                  <span class="text-sm text-gray-600">Enabled</span>
                </label>
              </div> -->
              <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div class="flex flex-col gap-2">
                  <label class="text-sm font-medium text-gray-600">Date</label>
                  <!-- <input v-model="closingDate" type="date" class="form-input" readonly /> -->
                   <span class="bg-white p-2 rounded-xl">{{ orderStore.formatDate(authStore.today) }}</span>
                </div>
                <div class="flex flex-col gap-2">
                  <label class="text-sm font-medium text-gray-600">POS Profile</label>
                  <span class="bg-white p-2 rounded-xl">
                    {{ authStore.getPosProfileName }}
                  </span>
                </div>
                <div class="flex flex-col gap-2">
                  <label class="text-sm font-medium text-gray-600">Cashier</label>
                  <span class="bg-white p-2 rounded-xl">
                    {{ authStore.getCasherName }}
                  </span>
                </div>
                <div class="flex flex-col gap-2">
                  <label class="text-sm font-medium text-gray-600">Float Amount From Closing</label>
                  <span class="bg-white p-2 rounded-xl">
                    ${{ authStore.openingFloat }}
                  </span>
                </div>
              </div>
            </section>

            <div class="relative border rounded-3xl mt-8 p-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
              <section>
                <button
                  type="button"
                  class="absolute right-8 top-8 px-6 py-2 text-purple-600 border-purple-200 rounded-xl font-medium hover:bg-purple-50 hover:border transition-colors"
                  @click.stop="resetFloat"
                >
                  Reset
                </button>
                <h2 class="text-lg font-semibold text-gray-800 mb-4">Bills</h2>
                <div class="grid grid-cols-1 xl:grid-cols-2 gap-4 max-h-[450px] overflow-y-auto pr-2">
                  <div
                    v-for="bill in bills"
                    :key="bill.id"
                    class="rounded-2xl border border-gray-100 bg-gray-100 shadow-sm p-4 transition"
                  >
                    <div class="flex items-start justify-between mb-1">
                      <div>
                        <p class="text-base font-semibold text-gray-900">{{ bill.label }}</p>
                      </div>
                    </div>
                    <div class="flex items-center justify-between">
                      <input
                        v-model.number="bill.quantity"
                        type="number"
                        min="0"
                        class="w-24 rounded-xl border border-gray-100 bg-white px-3 py-2 text-center text-base font-semibold text-gray-900 focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-100"
                        @input="handleQuantityInput(bill)"
                        @focus="selectQty"
                      />
                      <div class="text-right">
                        <p class="text-xs font-medium uppercase tracking-wide text-gray-400">Line Total</p>
                        <p class="text-lg font-semibold text-gray-900">{{ formatCurrency(lineTotal(bill)) }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2 class="text-lg font-semibold text-gray-800 mb-4">Coins</h2>
                <div class="grid grid-cols-1 xl:grid-cols-2 gap-4 max-h-[450px] overflow-y-auto pr-2">
                  <div
                    v-for="coin in coins"
                    :key="coin.id"
                    class="rounded-2xl border border-gray-100 bg-gray-100 shadow-sm p-4 transition"
                  >
                    <div class="flex items-start justify-between mb-1">
                      <div>
                        <p class="text-base font-semibold text-gray-900">{{ coin.label }}</p>
                      </div>
                    </div>
                    <div class="flex items-center justify-between">
                      <input
                        v-model.number="coin.quantity"
                        type="number"
                        min="0"
                        class="w-24 rounded-xl border border-gray-100 bg-white px-3 py-2 text-center text-base font-semibold text-gray-900 focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-100"
                        @input="handleQuantityInput(coin)"
                        @focus="selectQty"
                      />
                      <div class="text-right">
                        <p class="text-xs font-medium uppercase tracking-wide text-gray-400">Line Total</p>
                        <p class="text-lg font-semibold text-gray-900">{{ formatCurrency(lineTotal(coin)) }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>

            <div class="bg-gray-100 p-8 rounded-3xl grid grid-cols-1 md:grid-cols-4 gap-6 mt-8">
              <div class="rounded-2xl border border-gray-100 bg-white/80 backdrop-blur px-6 py-4 shadow-sm">
                <p class="text-sm font-medium text-gray-500">Bills Total</p>
                <p class="text-2xl font-semibold text-gray-900 mt-1">{{ formatCurrency(billTotal) }}</p>
              </div>
              <div class="rounded-2xl border border-gray-100 bg-white/80 backdrop-blur px-6 py-4 shadow-sm">
                <p class="text-sm font-medium text-gray-500">Coins Total</p>
                <p class="text-2xl font-semibold text-gray-900 mt-1">{{ formatCurrency(coinTotal) }}</p>
              </div>
              <div class="rounded-2xl border border-gray-200 bg-white px-6 py-4 shadow-md">
                <p class="text-sm font-medium text-gray-500">Cash Total</p>
                <p class="text-2xl font-semibold text-purple-700 mt-1">{{ formatCurrency(cashTotal) }}</p>
              </div>
              <div class="flex justify-end gap-4 mt-8">
                <button
                  type="button"
                  class="px-8 py-2 bg-purple-600 text-white rounded-xl font-semibold shadow-sm hover:bg-purple-700 transition-colors"
                  @click.stop="handleSubmit"
                  :disabled="isSubmitting"
                  :class="{ 'opacity-40 cursor-not-allowed': isSubmitting }"
                >
                  {{ isSubmitting ? "Submitting..." : "Submit" }}
                </button>

              </div>
            </div>

            
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-outer-enter-active,
.modal-outer-leave-active {
  transition: opacity 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12);
}

.modal-outer-enter-from,
.modal-outer-leave-to {
  opacity: 0;
}

.modal-inner-enter-active {
  transition: all 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12) 0.15s;
}

.modal-inner-leave-active {
  transition: all 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12);
}

.modal-inner-enter-from {
  opacity: 0;
  transform: scale(0.8);
}

.modal-inner-leave-to {
  transform: scale(0.8);
}
</style>